import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Card, CardContent } from "../../../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { Tooltip } from "../../../ui/tooltip";
import { Combobox } from "../../../ui/combobox";
import { operatorNeedsValue } from "../utils/validation";

import { 
  InfoIcon, 
  Trash2Icon, 
  PlusIcon, 
  ChevronDownIcon, 
  ChevronRightIcon
} from "lucide-react";
import { DecisionCondition, DecisionNodeData, DecisionVariablesRegistry } from "../utils/types";
import SingleSelectWithClear from "./ui/SingleSelectWithClear";

interface DecisionRuleBuilderProps {
  decisionData: DecisionNodeData;
  onChange: (decisionData: DecisionNodeData) => void;
  availableNodes: { id: string; label: string; entityType?: string; content?: string; identifier?: string }[];
  decisionVariablesRegistry: DecisionVariablesRegistry;
  globalVariableRegistry?: any;
  onUpdateEdges?: (decisionData: DecisionNodeData) => void;
  onCreateNodeForPath?: (pathType: "true" | "false") => Promise<string>;
}

// Helper function to generate unique condition IDs
const generateConditionId = () => `condition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const defaultCondition = (): DecisionCondition => ({
  id: generateConditionId(),
  variable: "",
      operator: "equals",
  value: "",
  type: "value"
});



const DecisionRuleBuilder: React.FC<DecisionRuleBuilderProps> = ({
  decisionData,
  onChange,
  availableNodes,
  decisionVariablesRegistry,
  globalVariableRegistry,
  onUpdateEdges,
  onCreateNodeForPath
}) => {
  
  
  // Collapsible state for Conditions section
  const [conditionsOpen, setConditionsOpen] = useState(true);

  // Helper function to get variable type from registry
  const getVariableType = (variableName: string): "boolean" | "string" | "number" => {
    // Check if this is a placeholder variable (extracted from content)
    if (globalVariableRegistry && globalVariableRegistry[variableName]) {
      const globalVar = globalVariableRegistry[variableName];
      // If variable has a persist field, it's a placeholder - always treat as string
      if (Object.prototype.hasOwnProperty.call(globalVar, "persist")) {
        return "string";
      }
    }
    
    const variable = decisionVariablesRegistry[variableName];
    return variable?.type || "string";
  };

  // Helper function to get default operator for variable type
  const getDefaultOperator = (type: "boolean" | "string" | "number"): "equals" | "not_equals" | "contains" | "greater_than" | "less_than" | "greater_than_or_equal" | "less_than_or_equal" | "is_true" | "is_false" | "is_null" | "is_not_null" | "not_contains" | "starts_with" | "ends_with" => {
    if (type === "boolean") {
      return "is_true";
    } else if (type === "number") {
      return "equals";
    } else {
      return "equals";
    }
  };

  // Handler for changing a specific condition (including joiner)
  const handleConditionChange = (idx: number, updates: Partial<DecisionCondition>) => {
    let finalUpdates = { ...updates };
    
    // If variable is being changed, automatically determine type and operator
    if (updates.variable !== undefined) {
      const variableType = getVariableType(updates.variable);
      const conditionType = variableType === "boolean" ? "boolean" : "value";
      finalUpdates = {
        ...finalUpdates,
        type: conditionType, // Keep legacy "boolean" or "value" for compatibility
        operator: getDefaultOperator(variableType),
        // Clear value if switching to boolean type
        value: variableType === "boolean" ? "" : updates.value || ""
      };
    }
    
    const newConditions = decisionData.conditions.map((cond, i) =>
      i === idx ? { ...cond, ...finalUpdates } : cond
    );
    onChange({ ...decisionData, conditions: newConditions });
  };

  // Handler for changing the joiner
  const handleJoinerChange = (idx: number, joiner: "and" | "or") => {
    const newConditions = decisionData.conditions.map((cond, i) =>
      i === idx ? { ...cond, joiner } : cond
    );
    onChange({ ...decisionData, conditions: newConditions });
  };

  // Handler for adding a new condition
  const handleAddCondition = () => {
    onChange({
      ...decisionData,
      conditions: [
        ...decisionData.conditions,
        { ...defaultCondition(), joiner: "and" }
      ]
    });
  };

  // Handler for removing a condition
  const handleRemoveCondition = (idx: number) => {
    const newConditions = decisionData.conditions.filter((_, i) => i !== idx);
    onChange({ ...decisionData, conditions: newConditions });
  };

  const handleTruePathChange = (nodeId: string) => {
    // console.log(`[DecisionRuleBuilder] True path changing from "${decisionData.truePath}" to "${nodeId}"`);
    const updatedData = {
      ...decisionData,
      truePath: nodeId || null
    };
    onChange(updatedData);
    // console.log("[DecisionRuleBuilder] Calling onUpdateEdges for true path change");
    onUpdateEdges?.(updatedData);
  };

  const handleFalsePathChange = (nodeId: string) => {
    // console.log(`[DecisionRuleBuilder] False path changing from "${decisionData.falsePath}" to "${nodeId}"`);
    const updatedData = {
      ...decisionData,
      falsePath: nodeId || null
    };
    onChange(updatedData);
    // console.log("[DecisionRuleBuilder] Calling onUpdateEdges for false path change");
    onUpdateEdges?.(updatedData);
  };

  // Helper function to get available operators for a variable type
  const getOperatorOptions = (type: "boolean" | "string" | "number") => {
    const commonOperators = [
      { value: "is_null", label: "is null" },
      { value: "is_not_null", label: "is not null" },
    ];

    if (type === "boolean") {
      return [
        { value: "is_true", label: "is true" },
        { value: "is_false", label: "is false" },
        { value: "equals", label: "equals" },
        { value: "not_equals", label: "not equals" },
        ...commonOperators,
      ];
    } else if (type === "number") {
      return [
        { value: "equals", label: "equals" },
        { value: "not_equals", label: "not equals" },
        { value: "greater_than", label: "greater than" },
        { value: "less_than", label: "less than" },
        { value: "greater_than_or_equal", label: "greater than or equal" },
        { value: "less_than_or_equal", label: "less than or equal" },
        ...commonOperators,
      ];
    } else { // string
      return [
        { value: "equals", label: "equals" },
        { value: "not_equals", label: "not equals" },
        { value: "contains", label: "contains" },
        { value: "not_contains", label: "does not contain" },
        { value: "starts_with", label: "starts with" },
        { value: "ends_with", label: "ends with" },
        ...commonOperators,
      ];
    }
  };

  return (
    <div className="max-h-96 overflow-y-auto">
      <div className="mb-4 flex items-center gap-2">
        <h3 className="text-sm font-gc-med text-foreground">Decision Logic</h3>
        <Tooltip content="Configure the logic for this decision node." side="right" immediate={true}>
          <InfoIcon className="w-4 h-4 text-maitai-green hover:text-maitai-green/80 transition-colors cursor-help" />
        </Tooltip>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4">
          <div
            className="flex items-center cursor-pointer select-none font-medium text-base"
            onClick={() => setConditionsOpen(open => !open)}
          >
            Conditions
            <span className="ml-2 flex items-center">
              {conditionsOpen ? (
                <ChevronDownIcon className="w-3 h-3" />
              ) : (
                <ChevronRightIcon className="w-3 h-3" />
              )}
            </span>
          </div>
          <div 
            className={`transition-all duration-300 ease-out ${conditionsOpen ? "" : "overflow-hidden"}`}
            style={{ 
              maxHeight: conditionsOpen ? "1000px" : "0px", 
              opacity: conditionsOpen ? 1 : 0, 
              transform: conditionsOpen ? "translateY(0)" : "translateY(-8px)" 
            }}
          >
            <div className="w-full pt-2 pb-2 space-y-2">
              {decisionData.conditions.map((condition, idx) => (
                <div key={condition.id || idx} className="flex items-center gap-2 mb-2 min-h-10">
                  {idx > 0 && (
                    <Select value={condition.joiner || "and"} onValueChange={value => handleJoinerChange(idx, value as "and" | "or")}>
                      <SelectTrigger className="w-20 flex-shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="and">AND</SelectItem>
                        <SelectItem value="or">OR</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <Combobox
                    value={condition.variable || ""}
                    onValueChange={(value) => handleConditionChange(idx, { variable: value })}
                    placeholder="Select variable..."
                    searchPlaceholder="Search variables..."
                    emptyMessage="No variables found"
                    options={Object.keys(decisionVariablesRegistry).map(key => ({
                      value: key,
                      label: key
                    }))}
                    className="flex-1 min-w-[100px]"
                  />
                  <Select value={condition.operator} onValueChange={value => handleConditionChange(idx, { operator: value as any })}>
                    <SelectTrigger className="w-32 flex-shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getOperatorOptions(condition.variable ? getVariableType(condition.variable) : "string").map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {operatorNeedsValue(condition.operator) && (
                    <Input
                      value={condition.value}
                      onChange={(e) => handleConditionChange(idx, { value: e.target.value })}
                      placeholder="Enter value"
                      className="h-8 flex-1 min-w-0"
                    />
                  )}
                  {idx > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveCondition(idx)}
                      className="ml-2 text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                    >
                      <Trash2Icon className="w-4 h-4" />
                    </Button>
                  )}
              </div>
            ))}
            <Button
              variant="outline"
              onClick={handleAddCondition}
              className="w-full"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Condition
            </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Next Node Paths */}
      <div className="mb-4 flex items-center gap-2">
        <h3 className="text-sm font-gc-med text-foreground">Next Node Paths</h3>
        <Tooltip content="Configure where the conversation goes based on the decision outcome." side="right" immediate={true}>
          <InfoIcon className="w-4 h-4 text-maitai-green hover:text-maitai-green/80 transition-colors cursor-help" />
        </Tooltip>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          {/* True Path */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground font-medium min-w-[80px]">True Path:</span>
            <SingleSelectWithClear
              value={decisionData.truePath}
              onChange={handleTruePathChange}
              placeholder="Select node"
              options={availableNodes
                .filter(node => node.identifier && (node.entityType === "BOT" || node.entityType === "DECISION"))
                .map(node => ({
                  label: node.identifier,
                  value: node.identifier,
                  meta: node.entityType || "NODE",
                  tooltip: node.content ? `${node.entityType}: ${node.identifier} - ${node.content}` : `${node.entityType}: ${node.identifier}`
                }))}
              className="flex-1"
            />
            {onCreateNodeForPath && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const newNodeIdentifier = await onCreateNodeForPath("true");
                    // Automatically select the newly created node
                    handleTruePathChange(newNodeIdentifier);
                  } catch (error) {
                    toast.error("Failed to create node for true path");
                  }
                }}
                className="gap-1 text-maitai-green border-maitai-green hover:bg-maitai-green hover:text-white"
                title="Create new node for True path"
              >
                <PlusIcon className="w-4 h-4" />
                Create
              </Button>
            )}
          </div>

          {/* False Path */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground font-medium min-w-[80px]">False Path:</span>
            <SingleSelectWithClear
              value={decisionData.falsePath}
              onChange={handleFalsePathChange}
              placeholder="Select node"
              options={availableNodes
                .filter(node => node.identifier && (node.entityType === "BOT" || node.entityType === "DECISION"))
                .map(node => ({
                  label: node.identifier,
                  value: node.identifier,
                  meta: node.entityType || "NODE",
                  tooltip: node.content ? `${node.entityType}: ${node.identifier} - ${node.content}` : `${node.entityType}: ${node.identifier}`
                }))}
              className="flex-1"
            />
            {onCreateNodeForPath && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const newNodeIdentifier = await onCreateNodeForPath("false");
                    // Automatically select the newly created node
                    handleFalsePathChange(newNodeIdentifier);
                  } catch (error) {
                    toast.error("Failed to create node for false path");
                  }
                }}
                className="gap-1 text-maitai-green border-maitai-green hover:bg-maitai-green hover:text-white"
                title="Create new node for False path"
              >
                <PlusIcon className="w-4 h-4" />
                Create
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DecisionRuleBuilder; 